// The device-local speech spool (C-7 SpeechSpool / SpoolSession).
//
// Audio is held on the device from the first frame of a capture until the
// user has what it was for: a dictation spool is removed when its text is
// inserted or discarded; a meeting deletes a range once the server has
// durably acknowledged it. Nothing here uploads anything.
//
// Writes are batched: one chunk (CHUNK_FRAMES frames) per backend write. The
// chunk being filled stays in memory; `flush()` persists it as it stands.
// Every backend operation of a session is serialized, and a chunk whose write
// failed stays in memory, so a storage error never loses audio held in this
// page.
import type { PcmFrame, SpeechSpool, SpoolMeta, SpoolSession } from '../contracts';
import {
  CHUNK_FRAMES,
  chunkStart,
  createMemorySpoolBackend,
  type SpoolBackend,
  type StoredChunk,
  type StoredFrame,
} from './backend';

const READ_PAGE_CHUNKS = 32;

type SpoolDeps = { now?: () => number };

const frameOf = (frame: StoredFrame): PcmFrame => ({
  seq: frame.seq,
  sampleOffset: frame.sampleOffset,
  samples: frame.samples,
  pcm: frame.pcm,
});

const inRange = (seq: number, from: number, to: number) => seq >= from && seq <= to;

class Session implements SpoolSession {
  private current: SpoolMeta;
  private pendingStart: number | null = null;
  private pending: StoredFrame[] = [];
  /** Committed chunks whose write has not succeeded yet (in flight or failed). */
  private readonly unsaved = new Map<number, StoredChunk>();
  private queue: Promise<void> = Promise.resolve();
  private removed = false;
  private writes = 0;
  private failures = 0;
  private writeFailed = false;
  private pendingRevision = 0;
  private savedRevision = 0;
  private readonly storageListeners = new Set<() => void>();
  private reportedState: 'pending' | 'stored' | 'failed' = 'stored';

  storageState(): 'pending' | 'stored' | 'failed' {
    if (this.writeFailed) return 'failed';
    return this.writes > 0 || this.pendingRevision !== this.savedRevision ? 'pending' : 'stored';
  }

  onStorageChange(listener: () => void) {
    this.storageListeners.add(listener);
    return () => this.storageListeners.delete(listener);
  }

  private storageChanged() {
    const state = this.storageState();
    if (state === this.reportedState) return;
    this.reportedState = state;
    for (const listener of this.storageListeners) listener();
  }

  private storageFailure() {
    this.failures += 1;
    this.writeFailed = true;
    this.storageChanged();
  }

  constructor(
    meta: SpoolMeta,
    private readonly backend: SpoolBackend,
    private readonly now: () => number,
  ) {
    this.current = meta;
  }

  meta(): SpoolMeta {
    return { ...this.current };
  }

  append(frame: PcmFrame): void {
    if (this.removed) return;
    const start = chunkStart(frame.seq);
    if (this.pendingStart !== null && start !== this.pendingStart) this.commitPending();
    this.pendingStart = start;
    this.pending = this.pending.filter((stored) => stored.seq !== frame.seq);
    this.pending.push({ ...frame });
    this.pendingRevision += 1;
    this.pending.sort((a, b) => a.seq - b.seq);
    this.current = {
      ...this.current,
      lastSeq: Math.max(this.current.lastSeq, frame.seq),
      samples: Math.max(this.current.samples, frame.sampleOffset + frame.samples),
      updatedAt: this.now(),
    };
    if (this.pending.length >= CHUNK_FRAMES) this.commitPending();
    this.storageChanged();
  }

  async flush(): Promise<void> {
    // Retry failed full chunks before reporting a successful local flush.
    await this.queue;
    const failures = this.failures;
    for (const chunk of this.unsaved.values()) this.persistChunk(chunk);
    this.persistPending();
    this.persistMeta();
    await this.queue;
    if (this.failures === failures && this.unsaved.size === 0) this.writeFailed = false;
    this.storageChanged();
  }

  async *frames(fromSeq = 0, toSeq = Number.MAX_SAFE_INTEGER): AsyncIterable<PcmFrame> {
    await this.queue;
    const memory = this.memoryChunks();
    let cursor = chunkStart(Math.max(0, fromSeq));
    const last = chunkStart(toSeq);
    while (cursor <= last) {
      const page = await this.backend
        .readChunks(this.current.id, cursor, last, READ_PAGE_CHUNKS)
        .catch((error: unknown) => {
          this.storageFailure();
          throw error;
        });
      const pageEnd = page.length === READ_PAGE_CHUNKS ? page[page.length - 1]!.firstSeq : last;
      const merged = new Map<number, StoredChunk>();
      for (const chunk of page) merged.set(chunk.firstSeq, chunk);
      for (const [start, chunk] of memory)
        if (start >= cursor && start <= pageEnd) merged.set(start, chunk);
      for (const start of [...merged.keys()].sort((a, b) => a - b))
        for (const frame of merged.get(start)!.frames)
          if (inRange(frame.seq, fromSeq, toSeq)) yield frameOf(frame);
      cursor = pageEnd + CHUNK_FRAMES;
    }
  }

  async deleteRange(fromSeq: number, toSeq: number): Promise<void> {
    if (this.pendingStart !== null) {
      this.pending = this.pending.filter((frame) => !inRange(frame.seq, fromSeq, toSeq));
      if (this.pending.length === 0) {
        this.pendingStart = null;
        this.savedRevision = this.pendingRevision;
      }
    }
    for (const [start, chunk] of [...this.unsaved]) {
      const kept = chunk.frames.filter((frame) => !inRange(frame.seq, fromSeq, toSeq));
      if (kept.length === 0) this.unsaved.delete(start);
      else this.unsaved.set(start, { ...chunk, frames: kept });
    }
    this.enqueue(() => this.deleteStored(fromSeq, toSeq));
    await this.queue;
  }

  /** Also persists the chunk being filled, so a kept session survives a reload whole. */
  async setState(
    state: SpoolMeta['state'],
    patch: Partial<SpoolMeta> = {},
    range?: { fromSeq: number; toSeq: number },
  ): Promise<void> {
    this.current = { ...this.current, ...patch, id: this.current.id, state, updatedAt: this.now() };
    await this.flush();
    if (!range) return;
    const chunks = new Map<number, StoredChunk>();
    for await (const frame of this.frames(range.fromSeq, range.toSeq)) {
      const first = chunkStart(frame.seq);
      let chunk = chunks.get(first);
      if (!chunk) chunks.set(first, (chunk = this.chunk(first, [])));
      chunk.frames.push(frame);
    }
    const meta = this.meta();
    const committed = await this.enqueue(() => this.backend.putSession(meta, [...chunks.values()]));
    if (!committed) throw new Error('speech_spool_checkpoint_failed');
  }

  async pcm(): Promise<Int16Array> {
    const parts: Int16Array[] = [];
    let total = 0;
    for await (const frame of this.frames()) {
      const samples = new Int16Array(frame.pcm, 0, frame.samples);
      parts.push(samples);
      total += samples.length;
    }
    const out = new Int16Array(total);
    let offset = 0;
    for (const part of parts) {
      out.set(part, offset);
      offset += part.length;
    }
    return out;
  }

  /** Called by the spool when the session is removed. */
  dispose() {
    this.removed = true;
    this.pending = [];
    this.pendingStart = null;
    this.unsaved.clear();
    this.storageListeners.clear();
  }

  private chunk(firstSeq: number, frames: StoredFrame[]): StoredChunk {
    return { sessionId: this.current.id, firstSeq, frames: [...frames] };
  }

  private commitPending() {
    if (this.pendingStart === null || this.pending.length === 0) return;
    const chunk = this.chunk(this.pendingStart, this.pending);
    this.pending = [];
    this.pendingStart = null;
    this.savedRevision = this.pendingRevision;
    this.unsaved.set(chunk.firstSeq, chunk);
    this.persistChunk(chunk);
    this.persistMeta();
  }

  private persistChunk(chunk: StoredChunk) {
    this.enqueue(async () => {
      await this.backend.putChunk(chunk);
      if (this.unsaved.get(chunk.firstSeq) === chunk) this.unsaved.delete(chunk.firstSeq);
    });
  }

  /** Writes the chunk being filled as it stands; it keeps filling in memory. */
  private persistPending() {
    if (this.pendingStart === null || this.pending.length === 0) return;
    const chunk = this.chunk(this.pendingStart, this.pending);
    const revision = this.pendingRevision;
    this.enqueue(async () => {
      await this.backend.putChunk(chunk);
      if (this.pendingRevision === revision) this.savedRevision = revision;
    });
  }

  private persistMeta() {
    const meta = this.meta();
    this.enqueue(() => this.backend.putSession(meta));
  }

  private memoryChunks(): Map<number, StoredChunk> {
    const memory = new Map(this.unsaved);
    if (this.pendingStart !== null && this.pending.length > 0)
      memory.set(this.pendingStart, this.chunk(this.pendingStart, this.pending));
    return memory;
  }

  private async deleteStored(fromSeq: number, toSeq: number) {
    const stored = await this.backend.readChunks(
      this.current.id,
      chunkStart(fromSeq),
      chunkStart(toSeq),
    );
    for (const chunk of stored) {
      const kept = chunk.frames.filter((frame) => !inRange(frame.seq, fromSeq, toSeq));
      if (kept.length === 0) await this.backend.deleteChunk(chunk.sessionId, chunk.firstSeq);
      else await this.backend.putChunk({ ...chunk, frames: kept });
    }
  }

  private enqueue(operation: () => Promise<void>) {
    let committed = false;
    this.writes += 1;
    this.storageChanged();
    this.queue = this.queue
      .then(async () => {
        if (this.removed) return;
        await operation();
        committed = true;
      })
      .catch(() => this.storageFailure())
      .finally(() => {
        this.writes -= 1;
        this.storageChanged();
      });
    return this.queue.then(() => committed);
  }
}

export type CreateSpoolOptions = SpoolDeps & {
  /** Resolves the durable backend, or null to fall back to memory. */
  backend?: () => Promise<SpoolBackend | null>;
  /** The durable guess before the backend has resolved. */
  expectDurable?: boolean;
};

export function createSpeechSpool(options: CreateSpoolOptions = {}): SpeechSpool {
  const now = options.now ?? (() => Date.now());
  let durable = options.expectDurable ?? false;
  let resolved: Promise<SpoolBackend> | null = null;
  const open = new Map<string, Session>();
  // A remove() that races an open() of the same id waits for it, so the open
  // still in flight never writes a removed session back.
  const opening = new Map<string, Promise<unknown>>();
  const backend = () =>
    (resolved ??= (options.backend ? options.backend() : Promise.resolve(null))
      .catch(() => null)
      .then((found) => {
        durable = Boolean(found);
        return found ?? createMemorySpoolBackend();
      }));

  const remove = async (id: string) => {
    await opening.get(id)?.catch(() => {});
    const session = open.get(id);
    open.delete(id);
    session?.dispose();
    await (await backend()).deleteSession(id).catch(() => {});
  };

  const openSession = async (input: Omit<SpoolMeta, 'updatedAt'>): Promise<SpoolSession> => {
    const store = await backend();
    const stored = await store.getSession(input.id);
    const meta: SpoolMeta = {
      ...input,
      lastSeq: Math.max(input.lastSeq, stored?.lastSeq ?? -1),
      samples: Math.max(input.samples, stored?.samples ?? 0),
      updatedAt: now(),
    };
    open.get(meta.id)?.dispose();
    const session = new Session(meta, store, now);
    open.set(meta.id, session);
    await session.flush();
    return session;
  };

  return {
    get durable() {
      return durable;
    },
    open(input) {
      const opened = openSession(input);
      opening.set(input.id, opened);
      const forget = () => {
        if (opening.get(input.id) === opened) opening.delete(input.id);
      };
      opened.then(forget, forget);
      return opened;
    },
    async list(owner, kind) {
      const sessions = await (await backend()).listSessions(owner);
      return sessions
        .filter((meta) => kind === undefined || meta.kind === kind)
        .map((meta) => open.get(meta.id)?.meta() ?? meta)
        .sort((a, b) => a.createdAt - b.createdAt);
    },
    remove,
    async purgeOwner(owner) {
      const sessions = await (await backend()).listSessions(owner).catch(() => []);
      for (const meta of sessions) await remove(meta.id);
      for (const [id, session] of open) if (session.meta().owner === owner) await remove(id);
    },
    async sweep(maxAgeMs) {
      const cutoff = now() - maxAgeMs;
      const sessions = await (await backend()).listSessions().catch(() => []);
      let removed = 0;
      for (const meta of sessions) {
        if (open.has(meta.id) || meta.updatedAt >= cutoff) continue;
        await remove(meta.id);
        removed += 1;
      }
      return removed;
    },
  };
}
